import React, { useCallback } from 'react';
import {
  arrayOf, bool, exact, func, string, number, objectOf, oneOf, oneOfType,
} from 'prop-types';
import styled, { css } from 'styled-components';
import { Droppable, DragDropContext } from 'react-beautiful-dnd';
import {
  Input, Flex, Typography, Checkbox, Dropdown, TextArea,
} from '@atoms';
import {
  ImageUploader, EditWarning, SpecialSizesModal,
} from '@molecules';
import {
  DraggableItems, SpecialProcessModal, AvailabilityProcessModal, EntitySpecials,
} from '@organisms';
import { Edit } from '@templates';
import { handleEnterKeyPress } from '@helpers/common';

const ItemProcess = ({
  onEditConfirm,
  onSpecialWarningModalClose,
  isSpecialWarningModalOpen,
  onSpecialAvailabilityAdd,
  onSpecialAvailabilityDetach,
  specialAvailabilities,
  isSpecialConfirmButtonDisabled,
  onSpecialDelete,
  isSpecialDeleteModalOpen,
  onSpecialDeleteModalChangeState,
  onSpecialEditSubmit,
  onSpecialAdd,
  onSizesSpecialModalChangeState,
  isSizesSpecialModalOpen,
  onSpecialEdit,
  selectedSizeSpecial,
  onSelectedSizeSpecialChange,
  isEditingSpecial,
  discountTypes,
  selectedDiscountType,
  onDiscountTypeChange,
  isSpecialModalOpen,
  onSpecialModalChangeState,
  onStartDateRemove,
  onEndDateRemove,
  onStartTimeRemove,
  onEndTimeRemove,
  onAvailabilityEdit,
  isAvailabilityConfirmButtonDisabled,
  onAvailabilityModalChangeState,
  isAvailabilityModalOpen,
  onAvailabilityDelete,
  onAvailabilityDeleteModalChangeState,
  onAvailabilityEditSubmit,
  isAvailabilityDeleteModalOpen,
  isAvailabilityEditing,
  startTime,
  endTime,
  startDate,
  endDate,
  onStartTimeChange,
  onEndTimeChange,
  onStartDateChange,
  onEndDateChange,
  onDayClick,
  selectedWeekDays,
  onAvailabilityNameChange,
  availabilityName,
  availabilities,
  onAvailabilityAdd,
  onAvailabilityDetach,
  error,
  isConfirmButtonDisabled,
  isWarningOpen,
  onWarningChangeState,
  onEditSize,
  availableSubcategories,
  onItemPriceChange,
  itemPrice,
  onDragSizeEnd,
  onItemDelete,
  isModalOpen,
  onModalChangeState,
  sizes,
  image,
  onImageChange,
  onItemActivityChange,
  isItemActive,
  onImageRemove,
  itemDescription,
  isLoading,
  itemName,
  onToastClose,
  onItemNameChange,
  onEditSubmit,
  onEditCancel,
  hasServerError,
  onItemDescriptionChange,
  onSizeAdd,
  isEditing,
  isDataTheSame,
  onSelectedSubcategoryChange,
  selectedSubcategory,
  itemSpecials,
}) => {
  const handleKeyPress = useCallback(
    isEditing && !isConfirmButtonDisabled ? handleEnterKeyPress(onEditSubmit) : null,
    [isEditing, isConfirmButtonDisabled, onEditSubmit],
  );

  const draggableSizes = sizes.map(({ id, name }) => ({
    id,
    name,
  }));

  return (
    <>
      <Edit
        error={error}
        isConfirmButtonDisabled={isConfirmButtonDisabled}
        onDelete={onItemDelete}
        isModalOpen={isModalOpen}
        name={itemName}
        onModalChangeState={onModalChangeState}
        isEditing={isEditing}
        isLoading={isLoading}
        onToastClose={onToastClose}
        onEditCancel={onEditCancel}
        onEditSubmit={onEditSubmit}
        hasServerError={hasServerError}
      >
        {!!availableSubcategories.length && !isEditing && (
          <DropdownContainer marginTop="xl2" marginBottom="sm" column itemsCenter justifyCenter>
            <Typography marginBottom="md" size="xl3" weight="black">
              Select subcategory
            </Typography>
            <Dropdown
              selectedOption={selectedSubcategory}
              options={availableSubcategories}
              onSelectedOptionChange={onSelectedSubcategoryChange}
            />
          </DropdownContainer>
        )}
        <Input
          marginTop="xl2"
          marginBottom="xl5"
          type="text"
          id="itemName"
          name="itemName"
          placeholder="Item name"
          value={itemName}
          onChange={onItemNameChange}
          onKeyPress={handleKeyPress}
          data-cy="itemName"
        />
        {!isEditing && (
          <Input
            marginBottom="xl5"
            type="text"
            id="itemPrice"
            name="itemPrice"
            placeholder="Item initial price"
            value={`$${itemPrice}`}
            onChange={onItemPriceChange}
            onKeyPress={handleKeyPress}
            data-cy="itemPrice"
          />
        )}
        <TextArea
          marginBottom="xl5"
          as="textarea"
          id="itemDescription"
          placeholder="Item description"
          value={itemDescription}
          onChange={onItemDescriptionChange}
          onKeyPress={handleKeyPress}
          type="text"
        />
        <ImageUploader
          image={image}
          onImageChange={onImageChange}
          onImageRemove={onImageRemove}
        />
        <Flex itemsCenter selfCenter marginTop="xl5">
          <Checkbox onCheckedStateChange={onItemActivityChange} isChecked={isItemActive} />
          <Typography marginLeft="lg" size="xl3" weight="black">Is item active?</Typography>
        </Flex>
        <Flex itemsCenter column paddingTop="xl5">
          <Typography marginBottom="md" size="xl3" weight="black">
            Sizes of current item
          </Typography>
          {!sizes.length && (
            <Flex justifyStart>
              <Typography size="lg" color="warmGrey" weight="black" marginBottom="lg">
                Nothing here yet
              </Typography>
            </Flex>
          )}
          <DragDropContext onDragEnd={onDragSizeEnd}>
            <Droppable droppableId="itemSizes">
              {provided => (
                <Flex column ref={provided.innerRef} {...provided.droppableProps}>
                  <DraggableItems
                    isDataTheSame={isDataTheSame}
                    onItemClick={onEditSize}
                    canBeAdded={isEditing}
                    onItemAdd={onSizeAdd}
                    items={draggableSizes}
                  />
                  {provided.placeholder}
                </Flex>
              )}
            </Droppable>
          </DragDropContext>
          <SpecialProcessModal
            onEditConfirm={onEditConfirm}
            onSpecialWarningModalClose={onSpecialWarningModalClose}
            isSpecialWarningModalOpen={isSpecialWarningModalOpen}
            onAvailabilityModalChangeState={onAvailabilityModalChangeState}
            onAvailabilityDetach={onSpecialAvailabilityDetach}
            availabilities={specialAvailabilities}
            onAvailabilityEdit={onAvailabilityEdit}
            onAvailabilityAdd={onSpecialAvailabilityAdd}
            onStartDateRemove={onStartDateRemove}
            onEndDateRemove={onEndDateRemove}
            onStartTimeRemove={onStartTimeRemove}
            onEndTimeRemove={onEndTimeRemove}
            isAvailabilityConfirmButtonDisabled={isAvailabilityConfirmButtonDisabled}
            onAvailabilityDeleteModalChangeState={onAvailabilityDeleteModalChangeState}
            startTime={startTime}
            endTime={endTime}
            startDate={startDate}
            endDate={endDate}
            onStartTimeChange={onStartTimeChange}
            onEndTimeChange={onEndTimeChange}
            onStartDateChange={onStartDateChange}
            onEndDateChange={onEndDateChange}
            onAvailabilityNameChange={onAvailabilityNameChange}
            onAvailabilityEditSubmit={onAvailabilityEditSubmit}
            availabilityName={availabilityName}
            isAvailabilityModalOpen={isAvailabilityModalOpen}
            onAvailabilityModalClose={onAvailabilityModalChangeState}
            isAvailabilityDeleteModalOpen={isAvailabilityDeleteModalOpen}
            onAvailabilityDelete={onAvailabilityDelete}
            onDayClick={onDayClick}
            selectedWeekDays={selectedWeekDays}
            isConfirmButtonDisabled={isSpecialConfirmButtonDisabled}
            onSpecialEditSubmit={onSpecialEditSubmit}
            entities={sizes}
            onSelectedEntityChange={onSelectedSizeSpecialChange}
            selectedEntity={selectedSizeSpecial}
            isEditing={isEditingSpecial}
            discountTypes={discountTypes}
            onSpecialDelete={onSpecialDelete}
            isSpecialDeleteModalOpen={isSpecialDeleteModalOpen}
            onSpecialDeleteModalChangeState={onSpecialDeleteModalChangeState}
            selectedDiscountType={selectedDiscountType}
            onDiscountTypeChange={onDiscountTypeChange}
            isModalOpen={isSpecialModalOpen}
            onModalClose={onSpecialModalChangeState}
          />
          <SpecialSizesModal
            onSizeSelect={onSelectedSizeSpecialChange}
            onModalClose={onSizesSpecialModalChangeState}
            isModalOpen={isSizesSpecialModalOpen}
            sizes={sizes}
          />
          <EditWarning isWarningOpen={isWarningOpen} onWarningClose={onWarningChangeState} />
          {isEditing && (
            <>
              <Typography marginBottom="md" marginTop="xl5" size="xl3" weight="black">
                Availability periods of item
              </Typography>
              <DragDropContext>
                <Droppable droppableId="itemAvailabilities">
                  {provided => (
                    <Flex column ref={provided.innerRef} {...provided.droppableProps}>
                      <DraggableItems
                        onItemClick={onAvailabilityEdit}
                        onItemRemove={onAvailabilityDetach}
                        onItemAdd={onAvailabilityAdd}
                        isDraggable={false}
                        canBeAdded={isEditing}
                        items={availabilities}
                      />
                      {provided.placeholder}
                    </Flex>
                  )}
                </Droppable>
              </DragDropContext>
              <EntitySpecials
                onSpecialEdit={onSpecialEdit}
                onSpecialAdd={onSpecialAdd}
                specials={itemSpecials}
              />
            </>
          )}
        </Flex>
        <AvailabilityProcessModal
          onStartDateRemove={onStartDateRemove}
          onEndDateRemove={onEndDateRemove}
          onStartTimeRemove={onStartTimeRemove}
          onEndTimeRemove={onEndTimeRemove}
          isConfirmButtonDisabled={isAvailabilityConfirmButtonDisabled}
          onAvailabilityDeleteModalChangeState={onAvailabilityDeleteModalChangeState}
          startTime={startTime}
          endTime={endTime}
          startDate={startDate}
          endDate={endDate}
          onStartTimeChange={onStartTimeChange}
          onEndTimeChange={onEndTimeChange}
          onStartDateChange={onStartDateChange}
          onEndDateChange={onEndDateChange}
          onAvailabilityNameChange={onAvailabilityNameChange}
          isEditing={isAvailabilityEditing}
          onAvailabilityEditSubmit={onAvailabilityEditSubmit}
          availabilityName={availabilityName}
          isModalOpen={isAvailabilityModalOpen}
          onModalClose={onAvailabilityModalChangeState}
          isAvailabilityDeleteModalOpen={isAvailabilityDeleteModalOpen}
          onAvailabilityDelete={onAvailabilityDelete}
          onDayClick={onDayClick}
          selectedWeekDays={selectedWeekDays}
        />
      </Edit>
    </>
  );
};

const DropdownContainer = styled(Flex)`
  ${() => css`
    width: 100%;
  `}
`;

ItemProcess.defaultProps = {
  selectedSubcategory: '',
  selectedSizeSpecial: null,
  error: '',
};

ItemProcess.propTypes = {
  onEditConfirm: func.isRequired,
  onSpecialWarningModalClose: func.isRequired,
  isSpecialWarningModalOpen: bool.isRequired,
  onSpecialAvailabilityAdd: func.isRequired,
  specialAvailabilities: arrayOf(exact({
    name: string,
    id: number,
  })).isRequired,
  onSpecialAvailabilityDetach: func.isRequired,
  isSpecialConfirmButtonDisabled: bool.isRequired,
  onSpecialDelete: func.isRequired,
  isSpecialDeleteModalOpen: bool.isRequired,
  onSpecialDeleteModalChangeState: func.isRequired,
  onSpecialEditSubmit: func.isRequired,
  onSpecialAdd: func.isRequired,
  onSizesSpecialModalChangeState: func.isRequired,
  isSizesSpecialModalOpen: bool.isRequired,
  onSelectedSizeSpecialChange: func.isRequired,
  selectedSizeSpecial: oneOfType([oneOf([null]), number]),
  onSpecialEdit: func.isRequired,
  isEditingSpecial: bool.isRequired,
  discountTypes: objectOf(exact({
    name: string,
    inputValue: string,
    inputHandler: func,
  })).isRequired,
  selectedDiscountType: string.isRequired,
  onDiscountTypeChange: func.isRequired,
  isSpecialModalOpen: bool.isRequired,
  onSpecialModalChangeState: func.isRequired,
  itemSpecials: arrayOf(exact({
    id: number,
    sizeName: string,
    available: bool,
    availabilityIds: arrayOf(number),
    sizeId: number,
    discountType: string,
    discountValue: number,
  })).isRequired,
  onStartDateRemove: func.isRequired,
  onEndDateRemove: func.isRequired,
  onStartTimeRemove: func.isRequired,
  onEndTimeRemove: func.isRequired,
  onAvailabilityEdit: func.isRequired,
  isAvailabilityConfirmButtonDisabled: bool.isRequired,
  onAvailabilityModalChangeState: func.isRequired,
  isAvailabilityModalOpen: bool.isRequired,
  onAvailabilityDelete: func.isRequired,
  onAvailabilityDeleteModalChangeState: func.isRequired,
  onAvailabilityEditSubmit: func.isRequired,
  isAvailabilityDeleteModalOpen: bool.isRequired,
  isAvailabilityEditing: bool.isRequired,
  startTime: string.isRequired,
  endTime: string.isRequired,
  startDate: string.isRequired,
  endDate: string.isRequired,
  onStartTimeChange: func.isRequired,
  onEndTimeChange: func.isRequired,
  onStartDateChange: func.isRequired,
  onEndDateChange: func.isRequired,
  onDayClick: func.isRequired,
  selectedWeekDays: arrayOf(number).isRequired,
  onAvailabilityNameChange: func.isRequired,
  availabilityName: string.isRequired,
  error: string,
  onAvailabilityDetach: func.isRequired,
  onAvailabilityAdd: func.isRequired,
  availabilities: arrayOf(exact({
    name: string,
    id: number,
  })).isRequired,
  isWarningOpen: bool.isRequired,
  onWarningChangeState: func.isRequired,
  onEditSize: func.isRequired,
  selectedSubcategory: number,
  availableSubcategories: arrayOf(exact({
    id: number,
    name: string,
  })).isRequired,
  onSelectedSubcategoryChange: func.isRequired,
  isConfirmButtonDisabled: bool.isRequired,
  isDataTheSame: bool.isRequired,
  onItemPriceChange: func.isRequired,
  itemPrice: string.isRequired,
  onDragSizeEnd: func.isRequired,
  onItemDelete: func.isRequired,
  isModalOpen: bool.isRequired,
  isEditing: bool.isRequired,
  onModalChangeState: func.isRequired,
  isItemActive: bool.isRequired,
  onItemActivityChange: func.isRequired,
  sizes: arrayOf(exact({
    price: number,
    id: number,
    name: string,
  })).isRequired,
  image: string.isRequired,
  onSizeAdd: func.isRequired,
  onImageChange: func.isRequired,
  onImageRemove: func.isRequired,
  onItemDescriptionChange: func.isRequired,
  itemDescription: string.isRequired,
  onToastClose: func.isRequired,
  hasServerError: bool.isRequired,
  isLoading: bool.isRequired,
  itemName: string.isRequired,
  onItemNameChange: func.isRequired,
  onEditSubmit: func.isRequired,
  onEditCancel: func.isRequired,
};

export default ItemProcess;
